import cx from 'classnames'
import { useContext, useEffect, useMemo } from 'react'

import { CartContext } from './cart/context'
import { ProductQuickAddContext } from './product/quick-add-context'
import { SearchContext } from './search-context'
import { SiteContext } from './site-context'

/**
 * Determines if stylesheet URL is added to document.
 */
const stylesheetExists = (url: string): boolean => {
  return [...document.styleSheets].some(
    (styleSheet) => styleSheet.href && styleSheet.href.indexOf(url) > -1,
  )
}

/**
 * Adds stylesheets to document.
 */
export const appendStylesheet = (url: string, nonce: string) => {
  // If stylesheet already exists, stop
  if (stylesheetExists(url)) {
    return
  }

  const link = document.createElement('link')
  link.href = url
  link.rel = 'stylesheet'
  link.type = 'text/css'
  link.setAttribute('nonce', nonce)

  document.head.appendChild(link)
}

/**
 * Gets CSS class that sets grid size.
 */
export const getGridSizeClass = (
  breakpoint?: string,
  size?: number,
  justify: string | boolean = false,
  align: string | boolean = false,
  start: number | boolean = false,
) => {
  const hasBreakpoint = breakpoint && breakpoint.trim()
  const colSpan = hasBreakpoint
    ? `${breakpoint}:col-span-${size}`
    : `col-span-${size}`
  const colStart = hasBreakpoint
    ? `${breakpoint}:col-start-${start}`
    : `col-start-${start}`
  const colJustify = hasBreakpoint ? `${breakpoint}:${justify}` : justify
  const colAlign = hasBreakpoint ? `${breakpoint}:${align}` : align

  return cx(
    colSpan,
    start && colStart,
    justify && colJustify,
    align && colAlign,
  )
}

/**
 * Gets CSS class that sets grid item order.
 */
export const getItemOrderClass = (
  index: number,
  breakpoint?: string,
  size?: number,
  totalSize?: number,
  reverseWhenFullWidth?: boolean,
) => {
  const hasBreakpoint = breakpoint && breakpoint.trim()
  const orderIndex =
    reverseWhenFullWidth && size === totalSize ? 12 - index : index + 1
  const order = hasBreakpoint
    ? `${breakpoint}:order-${Math.abs(orderIndex)}`
    : `order-${Math.abs(orderIndex)}`

  return cx(`${orderIndex < 0 ? '-' : ''}${order}`)
}

/**
 * Removes body overflow limitations.
 */
export const setBodyOverflow = (limited: boolean) => {
  document.body.classList.toggle('!overflow-hidden', limited)
}

/**
 * Removes body overflow limitations.
 */
export const clearBodyOverflow = () => {
  document.body.classList.remove('overflow-hidden')
  document.body.classList.remove('!overflow-hidden')
}

/**
 * Returns hooks that handles body overflow limitation.
 */
export const useManageBodyOverflow = () => {
  const { isCartModalOpen } = useContext(CartContext)
  const { productQuickAdd } = useContext(ProductQuickAddContext)
  const { searchDrawer } = useContext(SearchContext)
  const { megaNavigation, mobileMenu, videoModal } = useContext(SiteContext)

  const isOverflowLimited = useMemo(() => {
    return (
      isCartModalOpen ||
      megaNavigation.isOpen ||
      mobileMenu.isOpen ||
      productQuickAdd.isOpen ||
      searchDrawer.isOpen ||
      videoModal.isOpen
    )
  }, [
    isCartModalOpen,
    megaNavigation.isOpen,
    mobileMenu.isOpen,
    productQuickAdd.isOpen,
    searchDrawer.isOpen,
    videoModal.isOpen,
  ])

  useEffect(() => {
    setBodyOverflow(isOverflowLimited)
    document.body.classList.toggle('max-h-screen', isOverflowLimited)

    return () => {
      setBodyOverflow(false)
      document.body.classList.toggle('max-h-screen', false)
    }
  }, [isOverflowLimited])
}

/**
 * Gets element's absolute offset within document.
 */
export const getAbsoluteOffset = (element: HTMLElement) => {
  let currentElement: HTMLElement | null = element
  let offsetTop = 0
  let offsetLeft = 0

  do {
    offsetTop += currentElement.offsetTop ?? 0
    offsetLeft += currentElement.offsetLeft ?? 0
    currentElement = currentElement.offsetParent as HTMLElement | null
  } while (currentElement)

  return {
    top: offsetTop,
    left: offsetLeft,
  }
}
